import { useTranslation } from "react-i18next";
import { PageLayout } from "../../Components/PageLayout";
import { useNavigate, useParams } from "react-router-dom";
import { useFetch } from "../../Hooks/useFetch";
import instance from "../../Interceptor/api_instance";
import {
  Spinner,
  NewSpinner,
} from "../../Components/LoadingAnimations/LoadingAnimations";
import DataTable from "../../Components/DataTable/DataTable";
import SupplierSearch from "../../Components/Inputs/SupplierSearch";
import { useState, useEffect } from "react";
import { convertToPST, toDatetimeLocal } from "../../Helper/FormatDatetimes";
import { useApiMutation } from "../../Hooks/useApiMutation";
import { CreateReceiptCharge } from "./Modals/ReceiptCharges";
import { DeleteIcon, TrashIconCont } from "../../Media/Icons";
import {
  ActionFeedback,
  NewActionConfirm,
} from "../../Components/Modals/ActionResponses";
import CreateLineItem from "./Modals/LineItem";
import DropdownBtn from "../../Components/Buttons/DropdownBtn";
import UploadFile from "./Modals/UploadFile";
import { Button } from "../../Components/ui/button";

const ReceiptIdView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [receiptStatus, setReceiptStatus] = useState(false);

  // Get receipt ID from URL params and fetch receipt details
  const receiptId = useParams().id;
  const {
    data: receipt,
    setData,
    isLoading,
    error,
  } = useFetch(async () => {
    const res = await instance.get(`/safety/receipts/${receiptId}/`);
    const data = res.data;
    return {
      ...data,
      supplier: data.supplier
        ? { value: data.supplier.id, label: data.supplier.name }
        : null,
    };
  });

  useEffect(() => {
    if (receipt) {
      setReceiptStatus(receipt.status === "pending");
    }
  }, [receipt]);

  console.log(receiptStatus)
  // States
  const [feedback, setFeedback] = useState(null);
  const [createChargeModalOpen, setCreateChargeModalOpen] = useState(false);
  const [selectedCharge, setSelectedCharge] = useState(null);
  const [createLineItemModalOpen, setCreateLineItemModalOpen] = useState(false);
  const [selectedLineItem, setSelectedLineItem] = useState(null);
  const [actionConfirm, setActionConfirm] = useState(null);
  const [uploadFileModalOpen, setUploadFileModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // Save receipt details mutation
  const {
    mutate: saveReceipt,
    isLoading: isSaving,
    error: saveError,
  } = useApiMutation(async () => {
    const payload = {
      reference_id: receipt.reference_id,
      expected_date: receipt.expected_date,
      received_date: receipt.received_date,
      currency: receipt.currency,
      notes: receipt.notes,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      supplier: receipt.supplier ? receipt.supplier.value : null,
    };
    const res = await instance.patch(`safety/receipts/${receiptId}/`, payload);
    setFeedback({ message: "Receipt saved successfully", variant: "success" });
    return res;
  });

  // Delete line item mutation
  const {
    mutate: deleteLineItem,
    isLoading: isDeletingLineItem,
    error: deleteLineItemError,
  } = useApiMutation(async (lineItem) => {
    const res = await instance.delete(
      `/safety/receipts/line-items/${lineItem.id}/`,
    );
    if (res.status === 200) {
      setData((prev) => ({
        ...prev,
        line_items: prev.line_items.filter((item) => item.id !== lineItem.id),
      }));
      setSelectedLineItem(null);
      setActionConfirm(null);
      setFeedback({
        message: "Line item deleted successfully",
        variant: "success",
      });
    }
    return res;
  });

  // Delete charge mutation
  const {
    mutate: deleteCharge,
    isLoading: isDeletingCharge,
    error: deleteChargeError,
  } = useApiMutation(async (charge) => {
    const res = await instance.delete(`/safety/receipts/charges/${charge.id}/`);
    if (res.status === 200) {
      setData((prev) => ({
        ...prev,
        charges: prev.charges.filter((c) => c.id !== charge.id),
      }));
      setActionConfirm(null);
      setSelectedCharge(null);
      setFeedback({
        message: "Charge deleted successfully",
        variant: "success",
      });
    }
    return res;
  });

  // Confirm receipt mutation
  const {
    mutate: closeReceipt,
    isLoading: isClosing,
    error: closeError,
  } = useApiMutation(async () => {
    const res = await instance.post(`/safety/receipts/${receiptId}/close/`);
    if (res.status === 200) {
      setFeedback({
        message: "Receipt closed successfully",
        variant: "success",
      });
      navigate("/receipts");
    }
  });

  // Cancel receipt mutation
  const {
    mutate: cancelReceipt,
    isLoading: isCanceling,
    error: cancelError,
  } = useApiMutation(async () => {
    const res = await instance.post(`/safety/receipts/${receiptId}/cancel/`);
    if (res.status === 200) {
      setFeedback({
        message: "Receipt canceled successfully",
        variant: "success",
      });
      navigate("/receipts");
    }
  });

  // Get file content
  const {
    data: fileContent,
    isLoading: isLoadingFileContent,
    error: fileContentError,
  } = useFetch(
    async () => {
      const res = await instance.get(`/files/blobs/${selectedFile.id}/`);
      // Open file content in new tab
      const sasUrl = res.data.sas_url;
      window.open(sasUrl, "_blank");
      return res.data.content;
    },
    [selectedFile],
    { enabled: !!selectedFile },
  );

  const handleInputChange = (field, value) => {
    setData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const dropdownActions = [
    {
      label: "Save",
      onClick: saveReceipt,
    },
    {
      label: "Close",
      onClick: () =>
        setActionConfirm({
          type: "closeReceipt",
          title: "Close receipt?",
          message: "Once closed, the receipt cannot be edited.",
          confirm: () => closeReceipt(),
          close: () => setActionConfirm(null),
        }),
    },
    {
      label: "Cancel",
      onClick: () =>
        setActionConfirm({
          type: "cancelReceipt",
          title: "Cancel receipt?",
          message: "This action cannot be undone.",
          confirm: () => cancelReceipt(),
          close: () => setActionConfirm(null),
        }),
    },
  ];

  useEffect(() => {
    if (
      error ||
      saveError ||
      deleteLineItemError ||
      deleteChargeError ||
      closeError ||
      cancelError
    ) {
      setFeedback({
        message: "Failed to complete receipt action",
        variant: "error",
      });
    }
  }, [
    error,
    saveError,
    deleteLineItemError,
    deleteChargeError,
    closeError,
    cancelError,
  ]);

  const lineItemsColumns = [
    {
      key: "sku",
      header: "SKU",
      getValue: (row) => row.sku,
    },
    {
      key: "quantity",
      header: "Quantity",
      getValue: (row) => row.quantity,
    },
    {
      key: "unit_price",
      header: "Unit Price",
      getValue: (row) => row.unit_price,
    },
    receiptStatus && {
      key: "",
      header: "",
      render: (value, row) => (
        <TrashIconCont
          onClick={() =>
            setActionConfirm({
              type: "deleteLineItem",
              title: "Delete line item?",
              message: "This action cannot be undone.",
              confirm: () => deleteLineItem(row),
              close: () => setActionConfirm(null),
            })
          }
        />
      ),
    },
  ];

  const chargesColumns = [
    {
      key: "description",
      header: "Description",
      getValue: (row) => row.description,
    },
    {
      key: "amount",
      header: "Amount",
      getValue: (row) => row.amount,
    },
    receiptStatus && {
      key: "",
      header: "",
      render: (value, row) => (
        <TrashIconCont
          onClick={() =>
            setActionConfirm({
              type: "deleteCharge",
              title: "Delete charge?",
              message: "This action cannot be undone.",
              confirm: () => deleteCharge(row),
              close: () => setActionConfirm(null),
            })
          }
        />
      ),
    },
  ];

  const fileColumns = [
    {
      key: "blob_name",
      header: "File Name",
      getValue: (row) => row.blob_name,
    },
    {
      key: "size_bytes",
      header: "Size (KB)",
      getValue: (row) => Math.round(row.size_bytes / 1024),
    },
    {
      key: "uploaded_by",
      header: "Uploaded By",
      getValue: (row) => row.uploaded_by.full_name,
    },
    {
      key: "created_at",
      header: "Uploaded At",
      getValue: (row) => convertToPST(row.created_at),
    },
  ];

  return (
    <PageLayout title={"Receipt"}>
      {feedback && (
        <ActionFeedback
          message={feedback.message}
          variant={feedback.variant}
          close={() => setFeedback(null)}
        />
      )}
      {actionConfirm &&
        (() => {
          const actionStateMap = {
            closeReceipt: { requestLoading: isClosing, error: closeError },
            cancelReceipt: { requestLoading: isCanceling, error: cancelError },
            deleteLineItem: {
              requestLoading: isDeletingLineItem,
              error: deleteLineItemError,
            },
            deleteCharge: {
              requestLoading: isDeletingCharge,
              error: deleteChargeError,
            },
          };
          const { requestLoading, error: actionError } =
            actionStateMap[actionConfirm.type] ?? {};
          return (
            <NewActionConfirm
              title={actionConfirm.title}
              message={actionConfirm.message}
              warning={actionConfirm.warning}
              confirm={actionConfirm.confirm}
              close={actionConfirm.close}
              requestLoading={requestLoading}
              error={actionError}
            />
          );
        })()}

      {createLineItemModalOpen && (
        <CreateLineItem
          close={() => setCreateLineItemModalOpen(false)}
          locationId={receipt.location ? receipt.location.id : null}
          receiptId={receiptId}
          setData={setData}
          setFeedback={setFeedback}
        />
      )}
      {createChargeModalOpen && (
        <CreateReceiptCharge
          close={() => setCreateChargeModalOpen(false)}
          receiptId={receiptId}
          setData={(charge) =>
            setData((prev) => ({
              ...prev,
              charges: [...prev.charges, charge],
            }))
          }
          setFeedback={setFeedback}
        />
      )}
      {isLoading ? (
        <Spinner />
      ) : (
        <>
          <>
            <div className="space-between margin-bottom-24">
              <div className="flex-1">
                <h2 className="global-h2 black">
                  Receipt # {receipt.id} - {receipt.location.loc_name}
                </h2>
              </div>
              <div className="flex-1">
                <div style={{ position: "relative", display: "inline-block" }}>
                  {isSaving ? (
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <NewSpinner />
                    </div>
                  ) : receiptStatus ? (
                    <DropdownBtn
                      actions={dropdownActions}
                      className={"change-pwd-btn"}
                      disabled={isSaving}
                    />
                  ) : null}
                </div>
              </div>
            </div>
            <div className="margin-bottom-24">
              <div className="new-white-cont">
                <h4 className="global-h4 black border-bottom">
                  Receipt Details
                </h4>
                <div className="flex-between margin-bottom-24">
                  <div className="new-form-block">
                    <label>Supplier</label>
                    <SupplierSearch
                      selectedOption={receipt.supplier}
                      onSelect={(option) =>
                        handleInputChange("supplier", option)
                      }
                      isDisabled={!receiptStatus}
                    />
                  </div>
                  <div className="new-form-block">
                    <label>Reference Id</label>
                    <input
                      className="new-text-input with-border"
                      value={receipt.reference_id}
                      onChange={(e) =>
                        handleInputChange("reference_id", e.target.value)
                      }
                      type="text"
                      disabled={!receiptStatus}
                    />
                  </div>
                  <div className="new-form-block">
                    <label>Expected Date</label>
                    <input
                      className="new-text-input with-border"
                      value={toDatetimeLocal(receipt.expected_date)}
                      onChange={(e) =>
                        handleInputChange("expected_date", e.target.value)
                      }
                      type="datetime-local"
                      disabled={!receiptStatus}
                    />
                  </div>
                  <div className="new-form-block">
                    <label>Arrival Date</label>
                    <input
                      className="new-text-input with-border"
                      value={toDatetimeLocal(receipt.received_date)}
                      onChange={(e) =>
                        handleInputChange("received_date", e.target.value)
                      }
                      type="datetime-local"
                      disabled={!receiptStatus}
                    />
                  </div>
                  <div className="new-form-block">
                    <label>{t("general.currency")}</label>
                    <select
                      className="new-text-input with-border"
                      value={receipt.currency}
                      onChange={(e) =>
                        handleInputChange("currency", e.target.value)
                      }
                      disabled={!receiptStatus}
                    >
                      <option value="USD">USD</option>
                      <option value="MXN">MXN</option>
                    </select>
                  </div>
                </div>
                <div className="form-block">
                  <label>{t("forms.type.vehicle.labels.notes")}</label>
                  <div className="input-cont">
                    <textarea
                      className="text-input with-border no-resize"
                      rows={4}
                      value={receipt.notes}
                      onChange={(e) =>
                        handleInputChange("notes", e.target.value)
                      }
                    ></textarea>
                  </div>
                </div>
              </div>
            </div>

            <div className="new-white-cont margin-bottom-24">
              <h4 className="global-h4 black border-bottom">Line Items</h4>
              <div className="full-cont margin-bottom-24">
                {receiptStatus && (
                  <button
                    className="change-pwd-btn"
                    onClick={() => setCreateLineItemModalOpen(true)}
                  >
                    {t("buttons.addNew")}
                  </button>
                )}
              </div>
              <DataTable
                columns={lineItemsColumns}
                rows={receipt.line_items}
                isLoading={isLoading}
              />
            </div>

            <div className="new-white-cont margin-bottom-24">
              <h4 className="global-h4 black border-bottom">Receipt Charges</h4>
              <div className="full-cont margin-bottom-24">
                {receiptStatus && (
                  <button
                    className="change-pwd-btn"
                    onClick={() => setCreateChargeModalOpen(true)}
                  >
                    {t("buttons.addNew")}
                  </button>
                )}
              </div>
              <DataTable
                columns={chargesColumns}
                rows={receipt.charges}
                isLoading={isLoading}
              />
            </div>

            <div className="new-white-cont">
              <h4 className="global-h4 black border-bottom">Uploaded Files</h4>
              <div className="full-cont margin-bottom-24">
                {receiptStatus && (
                  <button
                    className="change-pwd-btn"
                    onClick={() => setUploadFileModalOpen(true)}
                  >
                    {t("buttons.addNew")}
                  </button>
                )}
              </div>
              <DataTable
                columns={fileColumns}
                rows={receipt.files}
                isLoading={isLoading}
                onRowClick={(row) => setSelectedFile(row)}
              />
              {uploadFileModalOpen && (
                <UploadFile
                  reference={{
                    id: receiptId,
                    model: "stock_receipt",
                    blob_type: "document",
                  }}
                  close={() => setUploadFileModalOpen(false)}
                  setData={(file) =>
                    setData((prev) => ({
                      ...prev,
                      files: [...prev.files, file],
                    }))
                  }
                />
              )}
            </div>
          </>
        </>
      )}
    </PageLayout>
  );
};

export default ReceiptIdView;
